import { all, put, select, takeLatest } from 'redux-saga/effects';
import { UsersTypes } from './users-action';
import { AlertActionType } from '@store/alert/alert-action';
import { usersStore } from './users-store';
import getServiceDate from '@api/appointment/service-date';
import getServiceFee from '@api/appointment/service-fee';
import getUserItemApi from '@api/users/user-item';
import getUsersFromAPI from '@api/users/users';
import usersSearchApi from '@api/users/users-search';
import { homeStore } from '@store/home-screen/home-store';
import getDictionary from '@dictionaries-client';

function* getUsersExtendedListWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { users: { messages } } } = getDictionary(locale);
  try {
    const { currentPage, filters, usersList } = yield select(usersStore);

    const response: { data: Array<object>, last_page: number, current_page: number } = yield getUsersFromAPI({
      sort: 'id',
      page: currentPage + 1,
      limit: currentPage === 0 ? 20 : 10,
      filters: {
        provinceId: filters?.province?.id,
        serviceTypeId: filters?.serviceType?.id,
        specialtiesTypeId: filters?.specialtiesType?.id,
        specialtyId: filters?.specialty?.id,
        workHour: filters?.workHour?.id,
        searchValue: filters?.search?.id,
      },
      cache: 'no-cache',
    });
    yield put({
      type: UsersTypes.SET_USERS_LIST,
      data: {
        usersList: [...usersList, ...response?.data],
        currentPage: response?.current_page,
        lastPage: response?.last_page,
      },
    });
  } catch (error : any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.default,
      severity: 'danger',
    });
  }
}

function* getUserDetailsWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { users: { messages } } } = getDictionary(locale);
  try {
    const { selectedUserSlug } = yield select(usersStore);

    const userItemResponse: { userContacts: Array<object>, userItem: { route_key: string } } = yield getUserItemApi({
      slug: selectedUserSlug,
      cache: 'no-cache',
    });

    yield put({
      type: UsersTypes.SET_USER_CONTACT,
      data: {
        selectedUserId: userItemResponse?.userItem?.route_key,
        userContacts: userItemResponse?.userContacts[0],
      },
    });

    if (userItemResponse?.userContacts[0]) {
      yield getUserServices();
    } else {
      yield put({
        type: UsersTypes.SET_USER_ITEM_LOADING,
      });
    }
  } catch (error : any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.doctor_address_error,
      severity: 'danger',
    });
    yield put({
      type: UsersTypes.SET_USER_ITEM_LOADING,
    });
  }
}

function* getUserServices() {
  const { locale } = yield select(homeStore);
  const { api: { users: { messages } } } = getDictionary(locale);
  try {
    const { selectedUserId, userDetails } = yield select(usersStore);

    const userItemServiceResponse: { data: Array<{ route_key_affix: string }> } = yield getServiceFee({
      userId: selectedUserId,
      contactId: userDetails?.userContacts?.route_key_affix,
      subTypeId: '1',
      cache: 'no-cache',
    });

    yield put({
      type: UsersTypes.SET_USER_SERVICE_ID,
      data: {
        selectedUserServiceId: userItemServiceResponse?.data[0]?.route_key_affix,
      },
    });

    if (userItemServiceResponse?.data[0]?.route_key_affix) {
      yield getUserFirstAppointment();
    } else {
      yield put({
        type: UsersTypes.SET_USER_ITEM_LOADING,
      });
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.no_service_to_offer,
      severity: 'danger',
    });
    yield put({
      type: UsersTypes.SET_USER_ITEM_LOADING,
    });
  }
}

function* getUserFirstAppointment() {
  const { locale } = yield select(homeStore);
  const { api: { users: { messages } } } = getDictionary(locale);
  try {
    const { selectedUserId, selectedUserServiceId, userDetails } = yield select(usersStore);

    const userItemServiceResponse: { data: Array<{ route_key_affix: string }> } = yield getServiceDate({
      userId: selectedUserId,
      serviceId: selectedUserServiceId,
      locationId: userDetails?.userContacts?.route_key_affix,
      cache: 'no-cache',
    });

    yield put({
      type: UsersTypes.SET_USER_FIRST_APPOINTMENT,
      data: {
        selectedUserServiceId: userItemServiceResponse?.data[0]?.route_key_affix,
      },
    });
  } catch (error : any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.no_free_time,
      severity: 'danger',
    });
    yield put({
      type: UsersTypes.SET_USER_ITEM_LOADING,
    });
  }
}

function* loadUserSearchListDataWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { users: { messages } } } = getDictionary(locale);
  try {
    const { searchValue } = yield select(usersStore);

    const response: { data: Array<object> } = yield usersSearchApi({ searchQuery: searchValue, cache: 'no-cache' });

    yield put({
      type: UsersTypes.SET_USER_SEARCH_LIST,
      data: response?.data,
    });
  } catch (error : any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.default,
      severity: 'danger',
    });
  }
}

function* usersMiddleware() {
  yield all([
    takeLatest(UsersTypes.GET_EXTENDED_USERS, getUsersExtendedListWatcher),
    takeLatest(UsersTypes.SET_FILTER_ITEMS, getUsersExtendedListWatcher),
    takeLatest(UsersTypes.GET_USER_DETAILS, getUserDetailsWatcher),
    takeLatest(UsersTypes.DELETE_FILTER_TAG, getUsersExtendedListWatcher),
    takeLatest(UsersTypes.GET_USER_SEARCH_LIST, loadUserSearchListDataWatcher),
  ]);
}

export default usersMiddleware;
